import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Database, Clock, Save, RotateCcw, Trash2, Plus,
  HardDrive, Download,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

export function BackupsList({
  backups,
  isSuperAdmin,
  formatBytes,
  onCreateClick,
  onDownload,
  onRestore,
  onDelete,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <HardDrive className="w-4 h-4" />
          Available Backups
        </CardTitle>
        <CardDescription>{backups.length} backup(s) available</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {backups.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Size</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Collections</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {backups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{backup.name}</p>
                        {backup.description && (
                          <p className="text-xs text-muted-foreground">{backup.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-mono text-sm">{format(new Date(backup.created_at), 'dd/MM/yyyy HH:mm')}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(backup.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      {formatBytes(backup.size_bytes)}
                    </td>
                    <td className="px-4 py-3">
                      {backup.is_auto ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          <Clock className="w-3 h-3 mr-1" />
                          Auto
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Save className="w-3 h-3 mr-1" />
                          Manual
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-muted-foreground">
                        {backup.collections_backed_up?.length || 0} collections
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {isSuperAdmin && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDownload(backup)}
                            title="Download as file"
                            data-testid={`btn-download-backup-${backup.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onRestore(backup)}
                            title="Restore from this backup"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => onDelete(backup)}
                            title="Delete backup"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Database className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No backups yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first backup to protect your data
            </p>
            {isSuperAdmin && (
              <Button onClick={onCreateClick}>
                <Plus className="w-4 h-4 mr-2" />
                Create Backup
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
